import { TranslateService } from '@ngx-translate/core';
import { REQUIREMENT_TYPE_I18N_KEYS } from './requirement-type-defaults';

/**
 * NEX-1056 Review (fboudouhi Info I1): einheitliche Lookup-Fallback-Logik fuer
 * die Anzeige der projekt-lokalen Vorgangstypen und Status-Werte. Wird sowohl
 * von den Pipes ({@code RequirementTypeLabelPipe}, {@code RequirementStatusLabelPipe})
 * als auch von der Filter-Bar ({@code requirements-filter-bar}) genutzt, damit
 * die Uebersetzungslogik nur an einer Stelle lebt.
 *
 * <p>Der Rohwert-Fallback greift in zwei Faellen: der Wert ist kein Default
 * (projekt-lokal angelegt oder importiert), oder der i18n-Key ist nicht
 * aufloesbar. Beides ist gewuenscht: importierte Trackernamen wie „Epic"
 * sollen verbatim erscheinen, und ein fehlender Sprachkey darf keinen
 * Uebersetzungsschluessel im UI zeigen.
 */
export type RequirementLabelScope = 'type' | 'status';

// null-Prototype, damit externe Werte wie „constructor" oder „toString" nicht
// ueber den Prototyp einen falschen Treffer erzeugen (Muster aus
// requirement-type-defaults.ts, fboudouhi Nachfassung NEX-1018).
function nullPrototypeMap(entries: readonly (readonly [string, string])[]): Readonly<Record<string, string>> {
  const target: Record<string, string> = Object.create(null);
  for (const [k, v] of entries) target[k] = v;
  return Object.freeze(target);
}

export const REQUIREMENT_STATUS_I18N_KEYS: Readonly<Record<string, string>> = nullPrototypeMap([
  ['Entwurf', 'DRAFT'],
  ['In Bearbeitung', 'IN_PROGRESS'],
  ['Im Review', 'IN_REVIEW'],
  ['Freigegeben', 'APPROVED'],
  ['Abgeschlossen', 'DONE'],
]);

/**
 * Uebersetzt einen Wert aus dem Vorgangstyp- oder Status-Katalog in sein
 * anzeigbares Label. Rohwert-Fallback bei unbekanntem Wert oder fehlender
 * Uebersetzung. Fuer leere/null-Werte wird der leere String zurueckgegeben —
 * die Pipe schneidet ihre Ausgabe auch dort ab.
 */
export function localizeRequirementLabel(
  scope: RequirementLabelScope,
  value: string | null | undefined,
  translate: TranslateService,
): string {
  if (value === null || value === undefined || value === '') return '';
  const keys = scope === 'type' ? REQUIREMENT_TYPE_I18N_KEYS : REQUIREMENT_STATUS_I18N_KEYS;
  const legacyKey = keys[value];
  if (!legacyKey) return value;
  const fullKey = `requirements.${scope}.${legacyKey}`;
  const translated = translate.instant(fullKey);
  return translated === fullKey ? value : translated;
}
